<script lang="ts">
	import { format } from 'svelte-i18n';
	import { replace } from 'svelte-spa-router';

	import type { Home } from 'src/types/Home';

	import { getHomeDisplayName } from 'src/helpers/houses/homes_helpers';
	import { switchUnit } from 'src/services/auth_v2';
	import { user_data } from 'src/stores/account/user';
	import { showGlobalLoader } from 'src/stores/ui/global-loader';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { advancedCardComponent } from 'src/stores/advanced_card';

	import HomeAvatar from 'src/components/Homes/HomeAvatar.svelte';
	import SwitchHomeConfirmModal from 'src/components/Homes/SwitchHomeConfirmModal.svelte';

	import { ActionsContainer, IconButton, toast } from '@Shelly/shelly-design-system';

	export let home: Home;

	let isSwitching = false;
	let showConfirm = false;

	$: subtitle = home.sharedByEmail
		? {
				value: $format('settings_shared_by_label', {
					default: 'Shared by {email}',
					values: { email: home.sharedByEmail },
				}),
				type: 'muted',
				textSmall: false,
			}
		: { value: '' };

	// "Current home" label + check icon on the owned-home cards (UnitItem).
	$: currentHomeInfo = home.isActive
		? {
				value: $format('settings_current_home_label', { default: 'Current home' }),
				icon: 'fas fa-circle-check',
			}
		: { value: '' };

	// Switch the active session into the shared home, mirroring the account
	// profile dropdown's "enter home" action.
	const onEnterHome = async (): Promise<void> => {
		if (isSwitching || home.isActive) return;

		isSwitching = true;
		showGlobalLoader.set(true);
		try {
			await switchUnit(home.id, home.sub != null ? String(home.sub) : undefined);
			replace('/');
		} catch (err) {
			console.error('[SharedHomeItem] switch failed', err);
			toast.open({
				type: 'danger',
				title: $format('error_switch_unit', { default: 'Failed to switch home' }),
			});
		} finally {
			isSwitching = false;
			showGlobalLoader.set(false);
		}
	};
</script>

<div class="shared-home-card">
	<svelte:component
		this={$advancedCardComponent}
		title={getHomeDisplayName(home.name, $user_data.email)}
		{subtitle}
		description={currentHomeInfo}
		selected={home.isActive}
	>
		<div class="d-inline-flex pr-s" slot="left">
			<HomeAvatar {home} size="l" />
		</div>
		<ActionsContainer type="simple" slot="right">
			<IconButton
				slot="first"
				icon="fa-regular fa-arrow-right-from-bracket"
				variant={$showNewLayout ? 'help-2' : 'secondary'}
				disabled={home.isActive || isSwitching}
				aria-label={$format('settings_enter_shared_home', { default: 'Enter home' })}
				on:click={() => (showConfirm = true)}
			/>
		</ActionsContainer>
	</svelte:component>
</div>

{#if showConfirm}
	<SwitchHomeConfirmModal
		bind:showModal={showConfirm}
		homeName={getHomeDisplayName(home.name, $user_data.email)}
		on:confirm={onEnterHome}
	/>
{/if}

<style>
	.shared-home-card :global(.text-color-muted) {
		font-weight: 500 !important;
	}

	.shared-home-card :global(.advanced-card) {
		overflow: visible;
	}
	.shared-home-card :global(.rectangular-avatar-wrapper .avatar-badge) {
		bottom: 0;
		right: 0;
		z-index: 4;
	}
</style>
